import { Box, Button, Container, Grid2, Pagination } from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { IconButton } from "@mui/material";
import '../../Components/Market/Market.css'
import Menu from '@mui/material/Menu';
import MenuItem from "@mui/material/MenuItem";
import './Dashboards.css'
import { useDispatch, useSelector } from "react-redux";
import { getWalletSummary } from "../../Actions/PortfolioAction";
import { formatCurrency, toFixed } from "../../Helper/helper";
import SkeletonTable from "../Skeleton/TableSkeleton";
import { Typography, Divider } from "@mui/material";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { StyledTableCell, StyledTableRow } from "../common/commonTableStyles";
import { closeInvestment, getInvestmentList } from "../../Services/investmentService";
import { useLocation, useNavigate } from "react-router-dom";
import PortfolioSkeleton from "../Skeleton/PortfolioSkeleton";
import { ToastError, ToastSuccess } from "../../CustomToaster/CustomToaster";
import socket from "../../utils/Socket";
import NoDataFound from "../NoDataFound/NoDataFound";

const RadioUnselected = () => (
    <Box
        sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            border: "2px solid #d1d1d1",
            mr: 1.5,
        }}
    />
);

const RadioSelected = () => (
    <Box
        sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            background:
                "conic-gradient(from 0deg, #ffcf55, #f5a623, #ffcf55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mr: 1.5,
        }}
    >
        <Box
            sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#ffffff",
            }}
        />
    </Box>
);

const cardStyle = {
    position: "relative",
    borderRadius: "16px",
    p: 3,
    color: "#fff",
    background:
        "var(--color-Portfolio)",
    overflow: "hidden",
    transition: "all 0.3s ease",
    height: '-webkit-fill-available',
    boxshadow: ' 0 1.411px 2.821px 0 rgba(16, 24, 40, 0.05)',

    border: '1px solid #1F242F',

    "&::before": {
        content: '""',
        position: "absolute",
        inset: 0,
        padding: "1px",
        borderRadius: "16px",
        background:
            " linear-gradient(90deg, #9E5E10 0%, #F8BC36 25%, #F8EB51 53.71%, #E89B02 78.37%, #874600 100%)",
        WebkitMask:
            "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
        WebkitMaskComposite: "xor",
        maskComposite: "exclude",
        opacity: 0,
        transition: "opacity 0.3s ease",
    },

    "&:hover::before": {
        opacity: 1,
    },
    "&:hover": {
        transform: "translateY(-4px)",
        border: '1px solid transparent',
    },
};

function ProfitGauge({ profit = 0, currentMonthPNLPercentage = 0, currency = "$" }) {
    const radius = 90;
    const circumference = Math.PI * radius;
    const isNegative = currentMonthPNLPercentage < 0;
    const percent = Math.min(Math.abs(currentMonthPNLPercentage), 100);
    const strokeDashoffset = circumference * (1 - percent / 100);
    const isProfit = profit >= 0;

    const gradientStart = isNegative ? "#ef4444" : "#22c55e";
    const gradientEnd = isNegative ? "#b91c1c" : "#16a34a";

    return (
        <svg width="100%" height="100%" viewBox="0 0 220 120" style={{ overflow: "visible" }}>

            {/* Background Arc */}
            <path d="M 20 100 A 90 90 0 0 1 200 100" fill="none" stroke="#2a2a2a" strokeWidth="14" strokeLinecap="round" />

            {/* Progress Arc */}
            <path d="M 20 100 A 90 90 0 0 1 200 100" fill="none" stroke="url(#gradient)" strokeWidth="14" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={strokeDashoffset} style={{ transition: "stroke-dashoffset 0.6s ease" }} />

            <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor={gradientStart} />
                    <stop offset="100%" stopColor={gradientEnd} />
                </linearGradient>
            </defs>

            {/* Profit Text */}
            <text x="110" y="90" textAnchor="middle" fontSize="18" fill={isNegative ? "#ef4444" : "#22c55e"} fontWeight="600" >
                {currency} {profit?.toLocaleString()}
            </text>

            <text x="110" y="108" textAnchor="middle" fontSize="12" fill="#aaa" >This Month
            </text>
        </svg>
    );
}

const Portfolio = () => {

    // Hooks
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // Selector
    const { userDetailData } = useSelector((state) => state.user);
    const { assets, totalCryptoBalanceUSDT = 0, totalCryptoBalanceUSD = 0, todaysPnl = 0, yesterdayPnl = 0, totalInvestedINR = 0, todaysProfit = 0, todaysPnlPercentage = 0, totalInvestedUSD = 0, totalInvestedUSDT = 0, totalProfitINR = 0, totalProfit = 0, loading: portfolioLoading, currentMonthPNL, currentMonthProfitUSDT, lastMonthProfitUSDT, currentMonthPNLPercentage } = useSelector((state) => state.portfolio);

    const queryParams = new URLSearchParams(location.search);
    const pageFromUrl = Number(queryParams.get("page")) || 1;

    // State
    const [showBalance, setShowBalance] = useState(true);
    const [showInvested, setShowInvested] = useState(true);
    const [investmentData, setInvestmentData] = useState({ data: [], totalPages: 0, currentPage: 1 });
    const [investmentLoadingId, setInvestmentLoadingId] = useState(null);
    const [currentPage, setCurrentPage] = useState(pageFromUrl);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [investmentLoading, setInvestmentLoading] = useState(true);
    const [tickers, setTickers] = useState([]);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [selectedRange, setSelectedRange] = React.useState("last_month");
    const [dynamicPnl, setDynamicPnl] = useState(todaysPnl);
    const [dynamicPnlPercentage, setDynamicPnlPercentage] = useState(todaysPnlPercentage);

    useEffect(() => {
        const handler = (data) => {
            setTickers(prev => {
                const index = prev.findIndex(t => t.symbol === data.symbol);
                if (index === -1) return [...prev, { ...data, lastPrice: data.lastPrice }];
                const updated = [...prev];
                updated[index] = { ...updated[index], ...data, lastPrice: data.lastPrice };
                return updated;
            });
        };
        socket.on("live_ticker_update", handler);
        return () => { socket.off("live_ticker_update", handler); };
    }, []);

    const tickerMap = useMemo(() => {
        const map = {};
        tickers?.forEach(t => {
            map[t.symbol] = t.lastPrice || 0;
        });
        return map;
    }, [tickers]);

    const calculateLivePnl = (row) => {
        const livePrice = tickerMap[row.pair];
        if (!livePrice || row.status !== "open") {
            return row.pnl || 0;
        }

        const entry = Number(row.entryPrice || 0);
        const qty = Number(row.qty || 0);
        if (!entry || !qty) return 0;

        const priceDiff = row.type === "buy" ? livePrice - entry : entry - livePrice;

        return Number((priceDiff * qty).toFixed(6));
    };

    useEffect(() => {
        if (userDetailData?._id) {
            dispatch(getWalletSummary(userDetailData._id));
            const payload = { page: currentPage, limit: rowsPerPage, filter: { status: "open" } };
            fetchInvestmentList(payload);
        }
    }, [dispatch, userDetailData?._id]);

    useEffect(() => {
        const payload = { page: currentPage, limit: rowsPerPage, filter: { status: "open" } };
        fetchInvestmentList(payload);
    }, [dispatch, currentPage, rowsPerPage]);

    const fetchInvestmentList = async (payload) => {
        setInvestmentLoading(true);
        try {
            const { data } = await getInvestmentList(payload);
            if (data?.success) {
                setInvestmentData(data.result);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setInvestmentLoading(false);
        }
    };

    const todayPnlColor = dynamicPnl > 0 ? "#22c55e" : dynamicPnl < 0 ? "#ef4444" : "#fbbf24";
    const todaysPnlPercentageColor = dynamicPnlPercentage > 0 ? "#22c55e" : dynamicPnlPercentage < 0 ? "#ef4444" : "#fbbf24";
    const totalProfitColor = totalProfit > 0 ? "#22c55e" : totalProfit < 0 ? "#ef4444" : "#fbbf24";

    const enrichedInvestments = useMemo(() => {

        return investmentData?.data?.map(row => {
            if (row.status !== "open") return row;

            const livePrice = tickerMap[row.pair];
            if (!livePrice) return row;

            const priceDiff = row.type === "buy" ? livePrice - row.entryPrice : row.entryPrice - livePrice;

            return { ...row, livePrice: livePrice, livePnl: Number((priceDiff * row.qty).toFixed(6)) };
        });
    }, [investmentData, tickerMap]);

    useEffect(() => {
        setDynamicPnl(toFixed(todaysPnl, 4));
        setDynamicPnlPercentage(toFixed(todaysPnlPercentage, 2));
    }, [todaysPnl, todaysPnlPercentage]);

    useEffect(() => {
        if (!Array.isArray(enrichedInvestments) || enrichedInvestments.length === 0) return;

        // Only status is open
        const openInvestments = enrichedInvestments.filter(row => row?.status === "open");
        if (openInvestments?.length === 0) return;

        const todayPnL = openInvestments.reduce((acc, row) => {
            if (row?.status !== "open") return acc;
            const pnl = Number(row?.livePnl);
            if (!Number.isFinite(pnl)) return acc;
            return acc + pnl;
        }, 0);

        const totalPnL = Number(todaysPnl || 0) + Number(todayPnL);

        const safeTotal = Number.isFinite(totalPnL) ? totalPnL : 0;

        setDynamicPnl(toFixed(safeTotal, 4));

        let percentage = 0;

        if (Number(totalCryptoBalanceUSDT) !== 0) {
            percentage = (safeTotal / Number(totalCryptoBalanceUSDT)) * 100;
        }

        if (!Number.isFinite(percentage)) percentage = 0;

        setDynamicPnlPercentage(toFixed(percentage, 2));

    }, [enrichedInvestments, totalCryptoBalanceUSDT]);

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleSelect = (value) => {
        setSelectedRange(value);
        handleCloseMenu();
    };

    const handlePageChange = (event, value) => {
        if (currentPage !== value) {
            setCurrentPage(value);
            if (value === 1) {
                navigate("");
            } else {
                navigate(`?page=${value}`);
            }
        };
    }

    const handleCloseInvestment = async (investmentId) => {
        try {
            // loading investment by investmentid
            setInvestmentLoadingId(investmentId);
            const res = await closeInvestment({ investmentId });
            if (res?.data?.success) {
                dispatch(getWalletSummary(userDetailData?._id, false));
                ToastSuccess({ message: res?.data?.message });
                // remove from investment list
                const updatedInvestmentData = investmentData?.data?.filter(item => item?.investmentId !== investmentId);
                setInvestmentData((prev) => {
                    return {
                        ...prev,
                        data: updatedInvestmentData,
                    };
                });
            } else {
                ToastError({ message: res.data.message || 'Something went wrong' });
            }
        } catch (error) {
            ToastError({ message: 'Something went wrong' });
        } finally {
            setInvestmentLoadingId(null);
        }
    }

    const renderInvestmentTable = ({ investments, page = 1, limit = 10 }) => (
        <Table sx={{ minWidth: 400, background: "var(--color-BasicBg)" }} stickyHeader aria-label="sticky table">
            <TableHead>
                <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Investment ID</TableCell>
                    <TableCell>Invested At</TableCell>
                    <TableCell>Pair</TableCell>
                    <TableCell>Buy Price</TableCell>
                    <TableCell>Live Price</TableCell>
                    <TableCell>Qty</TableCell>
                    {/* <TableCell>Value</TableCell> */}
                    <TableCell>PNL</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {investments?.length > 0 ? investments.map((row, index) => (
                    <StyledTableRow key={index}>
                        <StyledTableCell>{(page - 1) * limit + index + 1}</StyledTableCell>
                        <StyledTableCell>{row?.investmentId || "-"}</StyledTableCell>
                        <StyledTableCell>{row?.started_at || "-"}</StyledTableCell>
                        <StyledTableCell>{row?.pair || "-"}</StyledTableCell>
                        <StyledTableCell>{row?.entryPrice || "-"}</StyledTableCell>
                        <StyledTableCell>{row?.livePrice || "-"}</StyledTableCell>
                        <StyledTableCell>{row?.qty || "-"}</StyledTableCell>
                        {/* <StyledTableCell>{row?.pnl ? <span className={row?.pnl > 0 ? "green" : "red"}>{row?.pnl}</span> : "-"}</StyledTableCell> */}
                        <StyledTableCell>
                            {(() => {
                                const livePnl = calculateLivePnl(row);
                                return row?.status === "open" ? (
                                    <span className={livePnl > 0 ? "green" : livePnl < 0 ? "red" : ""}>
                                        {livePnl.toFixed(4)}
                                    </span>
                                ) : row?.pnl ? (
                                    <span className={row?.pnl > 0 ? "green" : "red"}>
                                        {row?.pnl}
                                    </span>
                                ) : "-";
                            })()}
                        </StyledTableCell>
                        <StyledTableCell className="capitalize" >{row?.status || "-"}</StyledTableCell>
                        <StyledTableCell>
                            {row?.status === 'open' ?
                                <div className="blu-btn">
                                    <Button
                                        variant="contained"
                                        disabled={investmentLoadingId === row?.investmentId}
                                        onClick={() => handleCloseInvestment(row?.investmentId)} color="primary" size="small">
                                        {investmentLoadingId === row?.investmentId ? "Closing..." : "Close"}
                                    </Button>
                                </div>
                                : '-'
                            }
                        </StyledTableCell>
                    </StyledTableRow>
                )) : (

                    <TableRow>
                        <TableCell colSpan={9} align="center">
                            <NoDataFound message="No active investments found" />
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    );

    return (
        <div>
            <Container className="xl-box" maxWidth="xl">
                <div className="display-2 mb-20" >
                    <h2 className="gold-color">Portfolio</h2>
                </div>

                {portfolioLoading ? <PortfolioSkeleton /> :
                    <>
                        <Grid2 container spacing={3}>
                            <Grid2 item size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 4 }}>
                                <Box sx={cardStyle}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography fontSize={16} sx={{ color: 'var(--color-PrimaryText)' }}>
                                            Crypto Balance
                                        </Typography>
                                        <IconButton size="small" onClick={() => setShowBalance(!showBalance)}>
                                            {showBalance ? <VisibilityOffOutlinedIcon sx={{ color: "#9ca3af" }} /> : <VisibilityOutlinedIcon sx={{ color: "#9ca3af" }} />}
                                        </IconButton>
                                    </Box>

                                    <Typography fontWeight={600} sx={{ color: 'var(--color-PrimaryText)', fontSize: { xs: '26px', md: '36px' } }}>
                                        {showBalance ? `${formatCurrency(totalCryptoBalanceUSDT || 0)} USDT` : "••••••"}
                                    </Typography>
                                    <Typography color="#fbbf24" className="gold-color5" fontSize="1.3rem" sx={{ mt: 2 }}>
                                        {showBalance ? `$${formatCurrency(totalCryptoBalanceUSD || 0)}` : "••••••"}
                                    </Typography>

                                    <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.12)" }} />

                                    <Box display="flex" justifyContent="space-between">
                                        <Box>
                                            <Typography fontSize={14} opacity={0.6} sx={{ color: 'var(--color-PrimaryText)' }}>TODAYS PNL</Typography>
                                            <div className="display-2">
                                                <Typography sx={{ mt: 2, color: showBalance ? todayPnlColor : "", fontWeight: 600 }}>
                                                    {showBalance ? `${dynamicPnl > 0 ? "+" : ""}$${formatCurrency(dynamicPnl)}` : "••••••"}
                                                    <span style={{ color: showBalance ? todayPnlColor : "" }}>{showBalance ? `(${toFixed(dynamicPnlPercentage, 4)}%)` : "••••••"}</span>
                                                </Typography>
                                                {/* <Typography sx={{ mt: 2, color: showBalance ? todaysPnlPercentageColor : "", fontWeight: 600 }}>
                                                    {showBalance ? `(${toFixed(dynamicPnlPercentage, 4)}%)` : "••••••"}
                                                </Typography> */}
                                            </div>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid2>

                            {/* Total Invested */}
                            <Grid2 item size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 4 }}>
                                <Box sx={cardStyle}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Typography fontSize={16} sx={{ color: 'var(--color-PrimaryText)' }}>Total Invested</Typography>
                                        <IconButton size="small" onClick={() => setShowInvested(!showInvested)}>
                                            {showInvested ? <VisibilityOffOutlinedIcon sx={{ color: "#9ca3af" }} /> : <VisibilityOutlinedIcon sx={{ color: "#9ca3af" }} />}
                                        </IconButton>
                                    </Box>

                                    <Typography fontWeight={600} sx={{ color: 'var(--color-PrimaryText)', fontSize: { xs: '26px', md: '36px' } }}>
                                        {showInvested ? `${formatCurrency(totalInvestedUSDT)} USDT` : "••••••"}
                                    </Typography>
                                    <Typography color="#fbbf24" className="gold-color5" fontSize="1.3rem" sx={{ mt: 2 }}>
                                        {showInvested ? `$${formatCurrency(totalInvestedUSD)}` : "••••••"}
                                    </Typography>

                                    <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.12)" }} />

                                    <Box display="flex" justifyContent="space-between">
                                        <Box>
                                            <Typography fontSize={14} opacity={0.6} sx={{ color: 'var(--color-PrimaryText)' }}>TOTAL RETURNS</Typography>
                                            <Typography sx={{ mt: 2, color: showInvested ? totalProfitColor : "", fontWeight: 600 }} >
                                                {showInvested ? `$${formatCurrency(totalProfit || 0)}` : "••••••"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid2>

                            {/* Profit Card */}
                            <Grid2 item size={{ xs: 12, sm: 6, md: 6, lg: 4, xl: 4 }}>
                                <Box sx={cardStyle}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center">
                                        <Typography fontSize={16} sx={{ color: 'var(--color-PrimaryText)' }}>Profit</Typography>
                                    </Box>
                                    {parseFloat(lastMonthProfitUSDT) > 0 &&
                                        <Typography fontSize={14} opacity={0.6} sx={{ color: 'var(--color-PrimaryText)' }} >
                                            {currentMonthPNLPercentage > 0
                                                ? `Profit is ${currentMonthPNLPercentage}% more than last month`
                                                : currentMonthPNLPercentage < 0
                                                    ? `Profit is ${Math.abs(currentMonthPNLPercentage)}% less than last month`
                                                    : "Profit is same as last month"}
                                        </Typography>
                                    }

                                    <Menu
                                        anchorEl={anchorEl}
                                        open={Boolean(anchorEl)}
                                        onClose={handleCloseMenu}
                                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                        transformOrigin={{ vertical: "top", horizontal: "right" }}
                                        PaperProps={{ sx: { mt: 1, minWidth: 200, borderRadius: 2, backgroundColor: "#ffffff", boxShadow: "0 8px 24px rgba(0,0,0,0.25)" } }}
                                    >
                                        <MenuItem onClick={() => handleSelect("last_month")}>
                                            {selectedRange === "last_month" ? <RadioSelected /> : <RadioUnselected />} Last month
                                        </MenuItem>
                                        <Divider />
                                        <MenuItem onClick={() => handleSelect("1_year")}>
                                            {selectedRange === "1_year" ? <RadioSelected /> : <RadioUnselected />} 1 year
                                        </MenuItem>
                                        <MenuItem onClick={() => handleSelect("5_year")}>
                                            {selectedRange === "5_year" ? <RadioSelected /> : <RadioUnselected />} 5 Year
                                        </MenuItem>
                                    </Menu>

                                    <Box position="relative" height={140} mt={1}>
                                        <ProfitGauge profit={currentMonthPNL} currentMonthPNLPercentage={currentMonthPNLPercentage} />
                                    </Box>
                                </Box>
                            </Grid2>
                        </Grid2>

                        <Grid2 container spacing={2} className="card-box mt-30" sx={{ alignItems: 'center' }}>
                            <Grid2 item size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                                <TableContainer sx={{ maxHeight: 600, scrollbarWidth: 'none', borderRadius: '12px 12px 12px 12px' }} className="mt-20">
                                    {investmentLoading ? <SkeletonTable rows={rowsPerPage} /> : renderInvestmentTable({ investments: enrichedInvestments, page: currentPage, limit: rowsPerPage })}
                                </TableContainer>
                                {/* {TablePaginations} */}

                                {!investmentLoading && enrichedInvestments?.length > 0 && investmentData?.totalPages > 1 && (
                                    <div className="Pagination-main">
                                        <Pagination page={currentPage} count={investmentData?.totalPages} onChange={handlePageChange} />
                                    </div>
                                )}
                            </Grid2>
                        </Grid2>
                    </>}
            </Container>
        </div>
    )
}

export default Portfolio